import { useRef, useState } from 'react'
import { cx, Icon } from './ui'
import './shared.css'

/**
 * F-141 (REQ-041-001): zona di trascinamento per i caricamenti. Trascina i file sopra l'area (oppure clic per
 * sceglierli): il componente NON carica nulla da solo, consegna i file a `onFiles`, che chiama lo stesso
 * `POST /api/documents` del caricamento classico (stesso endpoint, stessi limiti e stessi controlli del server).
 * Accessibile da tastiera: Invio o Spazio aprono la scelta del file.
 */
export function FileDropZone({
  onFiles,
  disabled = false,
  multiple = true,
  label = 'Trascina qui i file oppure clicca per sceglierli',
  busyLabel = 'Caricamento in corso...',
  busy = false,
  square = false,
}: {
  onFiles: (files: File[]) => void | Promise<void>
  disabled?: boolean
  multiple?: boolean
  label?: string
  busyLabel?: string
  busy?: boolean
  /** Riquadro piccolo e quadrato, da mettere in fila con altri. */
  square?: boolean
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const inactive = disabled || busy

  function deliver(list: FileList | null): void {
    if (inactive || !list || list.length === 0) return
    const files = Array.from(list)
    void onFiles(multiple ? files : files.slice(0, 1))
  }

  return (
    <div
      role="button"
      tabIndex={inactive ? -1 : 0}
      aria-disabled={inactive}
      className={cx('ecrm-dropzone', square && 'ecrm-dropzone--square', dragging && 'ecrm-dropzone--dragging', inactive && 'ecrm-dropzone--inactive')}
      onClick={() => { if (!inactive) inputRef.current?.click() }}
      onKeyDown={e => {
        if ((e.key === 'Enter' || e.key === ' ') && !inactive) {
          e.preventDefault()
          inputRef.current?.click()
        }
      }}
      onDragEnter={e => { e.preventDefault(); if (!inactive) setDragging(true) }}
      onDragOver={e => { e.preventDefault(); if (!inactive) setDragging(true) }}
      onDragLeave={e => { e.preventDefault(); setDragging(false) }}
      onDrop={e => {
        e.preventDefault()
        setDragging(false)
        deliver(e.dataTransfer.files)
      }}
    >
      <Icon name="upload_file" size={20} />
      {busy ? busyLabel : label}
      <input
        ref={inputRef}
        type="file"
        multiple={multiple}
        className="d-none"
        disabled={inactive}
        onChange={e => {
          deliver(e.target.files)
          e.target.value = ''
        }}
      />
    </div>
  )
}
